// Pieces Growth › Signups shares with Inquiries: the outreach badge and where
// someone came from (the landing page's attribution).

import type { ReactNode } from 'react'
import type { OutreachStatus } from '@/api/growthSignups'
import type { Touch, VisitAttribution } from '@/api/types'
import { Badge, KeyValueList } from '@/components/ui'
import { formatDateTime } from '@/lib/format'
import { OUTREACH_LABELS, OUTREACH_TONE } from '@/lib/signups'
import { SafeExternalLink } from './shared'

export function OutreachBadge({ status }: { status: OutreachStatus }) {
  return <Badge tone={OUTREACH_TONE[status]}>{OUTREACH_LABELS[status]}</Badge>
}

/** One visit's facts, each shown only when the landing page sent it. Every value is text. */
function touchItems(t: Touch | null | undefined) {
  if (!t) return []
  const items: Array<{ label: string; value: ReactNode }> = []
  if (t.source) items.push({ label: 'Source', value: t.medium ? `${t.source} / ${t.medium}` : t.source })
  else if (t.medium) items.push({ label: 'Medium', value: t.medium })
  if (t.campaign) items.push({ label: 'Campaign', value: t.campaign })
  if (t.term) items.push({ label: 'Term', value: t.term })
  if (t.content) items.push({ label: 'Content', value: t.content })
  if (t.referrer) items.push({ label: 'Referrer', value: <SafeExternalLink href={t.referrer} /> })
  if (t.landing_page) items.push({ label: 'Landing page', value: <SafeExternalLink href={t.landing_page} /> })
  if (t.at) items.push({ label: 'When', value: formatDateTime(t.at) })
  return items
}

/** First and last touch, side by side from the tablet up. */
export function TouchesView({
  first,
  last,
  lastLabel = 'Signed up on',
  extra = [],
}: {
  first: Touch | null | undefined
  last: Touch | null | undefined
  lastLabel?: string
  extra?: Array<{ label: string; value: ReactNode }>
}) {
  const firstItems = touchItems(first)
  const lastItems = touchItems(last)
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="min-w-0">
        <p className="mb-2 text-2xs font-bold uppercase tracking-eyebrow text-muted">First visit</p>
        {firstItems.length ? <KeyValueList items={firstItems} /> : <p className="text-[13px] text-muted">Nothing recorded.</p>}
      </div>
      <div className="min-w-0">
        <p className="mb-2 text-2xs font-bold uppercase tracking-eyebrow text-muted">{lastLabel}</p>
        {lastItems.length ? <KeyValueList items={lastItems} /> : <p className="text-[13px] text-muted">Nothing recorded.</p>}
      </div>
      {extra.length ? (
        <div className="min-w-0 sm:col-span-2">
          <KeyValueList items={extra} />
        </div>
      ) : null}
    </div>
  )
}

/** An inquiry's attribution, in the same words. */
export function VisitAttributionView({ attribution }: { attribution: VisitAttribution | null }) {
  if (!attribution) return <p className="text-[13px] text-muted">The landing page didn't say.</p>
  const ids = (['gclid', 'fbclid', 'msclkid', 'ga_client_id'] as const).filter((k) => attribution[k])
  return (
    <TouchesView
      first={attribution.first_touch}
      last={attribution.last_touch}
      lastLabel="Last visit"
      extra={ids.length ? [{ label: 'Ad click ids', value: <span className="break-all font-mono text-xs">{ids.map((k) => `${k}: ${attribution[k]}`).join(', ')}</span> }] : []}
    />
  )
}
